import React, { useEffect, useMemo, useState } from "react";
import { FONT_HEADING } from "../../../theme";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {
  getSettingByType,
  upsertSetting,
  deleteSettingById,
} from "../../../APICalling/settingApi";
import {
  getZohoModules,
  getZohoModuleFields,
} from "../../../APICalling/zohoAdminApi";
import { EXTENSION_IDENTIFIER } from "../../../config/serviceConstant";
import {
  getOpenSnakSuccessData,
  getOpenSnakErrorData,
} from "../../../config/otherConstant";

// Extension-owned defaults for new relatedList settings
const DEFAULT_ID_FIELD = `${EXTENSION_IDENTIFIER}__Shopify_ID_EXT`;
const DEFAULT_SHOP_URL_FIELD = `${EXTENSION_IDENTIFIER}__Shopify_Shop_URL`;

// Hide the noisy extension prefix from API names when displaying them — the
// client doesn't care that fields are namespaced. Tooltip on hover shows the
// full technical name.
const EXT_PREFIX_RE = new RegExp(`^${EXTENSION_IDENTIFIER}__`);
const prettifyFieldName = (apiName) =>
  (apiName || "").replace(EXT_PREFIX_RE, "").replace(/_/g, " ");

const cellSx = { fontSize: 14, py: 1.5, px: 2, borderColor: "#f4f4f5" };
const headCellSx = {
  ...cellSx,
  fontWeight: 600,
  color: "#71717a",
  bgcolor: "#fafafa",
  fontSize: 13,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  py: 1.25,
};

// Map a raw setting row from the API into the local config shape.
const mapSettingToConfig = (s) => {
  if (!s) return null;
  let schema = s.settingSchema ?? s.setting_schema ?? s.schema ?? {};
  if (typeof schema === "string") {
    try {
      schema = JSON.parse(schema);
    } catch {
      schema = {};
    }
  }
  const id = s.settingId ?? s.setting_id ?? s.id ?? "";
  const moduleApiName =
    s.moduleApiName ?? s.module_api_name ?? s.module_name ?? schema?.module ?? "";
  return {
    id,
    module: moduleApiName || "",
    IdField: schema.IdField || schema.idField || "",
    shopUrlField: schema.shopUrlField || schema.ShopUrlField || "",
    // Custom modules only: which Shopify resource the module mirrors
    // ("customers" | "orders" | "products"). Standard modules resolve their
    // renderer from the entity registry and don't need this.
    resource: schema.resource || "",
  };
};

// Standard modules whose widget renderer is hard-wired in the entity
// registry (RelatedList/entityRegistry.js). Any OTHER module is a custom
// module and must declare the Shopify resource it displays.
const STANDARD_WIDGET_MODULES = new Set([
  "Contacts",
  "Accounts",
  "Deals",
  "Sales_Orders",
  "Products",
]);

const RESOURCE_OPTIONS = [
  { value: "customers", label: "Customer (Shopify customer profile)" },
  { value: "orders", label: "Order (Shopify order details)" },
  { value: "products", label: "Product (Shopify product & inventory)" },
];

export default function RecordViewSetting({
  orgId,
  apiKey,
  dataCenterUrl,
  setSnack,
}) {
  const settingAuth = useMemo(
    () => ({ orgId, apiKey, dataCenterUrl }),
    [orgId, apiKey, dataCenterUrl]
  );

  // List state
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [moduleFilter, setModuleFilter] = useState("all");

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [moduleName, setModuleName] = useState("");
  const [idField, setIdField] = useState(DEFAULT_ID_FIELD);
  const [shopUrlField, setShopUrlField] = useState(DEFAULT_SHOP_URL_FIELD);
  // What the autocomplete input visually shows (prettified) — separate from
  // the API-name value above so the input reads "Shopify ID EXT" instead of
  // "easyshopifyforzohocrm__Shopify_ID_EXT".
  const [idFieldDisplay, setIdFieldDisplay] = useState("");
  const [shopUrlFieldDisplay, setShopUrlFieldDisplay] = useState("");
  // Custom modules only — which Shopify resource the widget renders.
  const [resource, setResource] = useState("");

  // Zoho metadata
  const [zohoModules, setZohoModules] = useState([]);
  const [zohoFields, setZohoFields] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // ── Fetch relatedList settings ──
  const refreshConfigs = async () => {
    setLoading(true);
    const list = await getSettingByType(settingAuth, "relatedList");
    setConfigs((list || []).map(mapSettingToConfig).filter(Boolean));
    setLoading(false);
  };

  useEffect(() => {
    if (!orgId || !apiKey || !dataCenterUrl) return;
    refreshConfigs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, apiKey, dataCenterUrl]);

  // ── Fetch Zoho modules once the modal is opened (cached) ──
  useEffect(() => {
    if (!modalOpen || zohoModules.length > 0) return;
    (async () => {
      const mods = await getZohoModules(window.ZOHO);
      setZohoModules(mods || []);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  // ── Fetch fields for the selected module ──
  useEffect(() => {
    if (!modalOpen || !moduleName) return;
    let cancelled = false;
    (async () => {
      setFieldsLoading(true);
      const fields = await getZohoModuleFields(window.ZOHO, moduleName);
      if (cancelled) return;
      setZohoFields(fields || []);
      setFieldsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [modalOpen, moduleName]);

  // ── Open modal (create or edit) ──
  const openModal = (configId = null) => {
    setEditingId(configId);
    let nextModule = "";
    let nextId = DEFAULT_ID_FIELD;
    let nextShop = DEFAULT_SHOP_URL_FIELD;
    let nextResource = "";
    if (configId) {
      const cfg = configs.find((c) => c.id === configId);
      if (cfg) {
        nextModule = cfg.module;
        nextId = cfg.IdField || DEFAULT_ID_FIELD;
        nextShop = cfg.shopUrlField || DEFAULT_SHOP_URL_FIELD;
        nextResource = cfg.resource || "";
      }
    }
    setModuleName(nextModule);
    setIdField(nextId);
    setIdFieldDisplay(prettifyFieldName(nextId));
    setShopUrlField(nextShop);
    setShopUrlFieldDisplay(prettifyFieldName(nextShop));
    setResource(nextResource);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  // ── Save (create/update) ──
  const isCustomModule = !!moduleName && !STANDARD_WIDGET_MODULES.has(moduleName);

  const handleSave = async () => {
    if (!moduleName) {
      setSnack(getOpenSnakErrorData("Please choose a Zoho module."));
      return;
    }
    // Custom modules must declare which Shopify resource the widget renders —
    // without it the related-list widget can't pick a view for the module.
    if (isCustomModule && !resource) {
      setSnack(
        getOpenSnakErrorData(
          "Please choose which Shopify data this module should display."
        )
      );
      return;
    }
    // Block duplicate modules when creating (one record view per module)
    if (!editingId) {
      const dup = configs.find((c) => c.module === moduleName);
      if (dup) {
        setSnack(
          getOpenSnakErrorData(
            `A record view already exists for ${moduleName}.`
          )
        );
        return;
      }
    }
    setSaving(true);
    const result = await upsertSetting(settingAuth, {
      settingId: editingId || "",
      settingSchema: {
        IdField: idField || DEFAULT_ID_FIELD,
        shopUrlField: shopUrlField || DEFAULT_SHOP_URL_FIELD,
        // Only meaningful for custom modules; standard modules resolve their
        // renderer from the entity registry.
        ...(isCustomModule && resource ? { resource } : {}),
      },
      moduleApiName: moduleName,
      settingType: "relatedList",
    });
    setSaving(false);
    if (!result) {
      setSnack(getOpenSnakErrorData("Failed to save record view."));
      return;
    }
    setSnack(
      getOpenSnakSuccessData(
        editingId ? "Record view updated." : "Record view created."
      )
    );
    closeModal();
    refreshConfigs();
  };

  // ── Delete ──
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const ok = await deleteSettingById(settingAuth, deleteTarget.id);
    setDeleting(false);
    setDeleteTarget(null);
    if (!ok) {
      setSnack(getOpenSnakErrorData("Failed to delete record view."));
      return;
    }
    setSnack(getOpenSnakSuccessData("Record view deleted."));
    refreshConfigs();
  };

  // ── Filter + search ──
  const moduleOptions = useMemo(
    () => [...new Set(configs.map((c) => c.module).filter(Boolean))].sort(),
    [configs]
  );
  const displayedConfigs = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return configs.filter((c) => {
      if (moduleFilter !== "all" && c.module !== moduleFilter) return false;
      if (!q) return true;
      return (
        c.module.toLowerCase().includes(q) ||
        c.IdField.toLowerCase().includes(q) ||
        c.shopUrlField.toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q)
      );
    });
  }, [configs, searchText, moduleFilter]);

  // Field options for the two field pickers — keep extension-owned defaults
  // available even when they haven't been loaded from the Zoho fields API.
  const fieldOptionsForIdField = useMemo(() => {
    const names = new Set(zohoFields.map((f) => f.api_name || f));
    names.add(DEFAULT_ID_FIELD);
    if (idField) names.add(idField);
    return [...names].sort();
  }, [zohoFields, idField]);
  const fieldOptionsForShopUrl = useMemo(() => {
    const names = new Set(zohoFields.map((f) => f.api_name || f));
    names.add(DEFAULT_SHOP_URL_FIELD);
    if (shopUrlField) names.add(shopUrlField);
    return [...names].sort();
  }, [zohoFields, shopUrlField]);

  // Modules not already used (for the create form). In edit mode, allow the
  // currently-edited module so the picker can show it.
  const availableModules = useMemo(() => {
    const used = new Set(
      configs
        .filter((c) => !editingId || c.id !== editingId)
        .map((c) => c.module)
    );
    return zohoModules.filter((m) => {
      const name = m.api_name || m;
      return !used.has(name);
    });
  }, [zohoModules, configs, editingId]);

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: FONT_HEADING,
              fontSize: 18,
              fontWeight: 600,
              letterSpacing: "-0.02em",
            }}
          >
            Record View Setting
          </Typography>
          <Typography sx={{ fontSize: 14, color: "#71717a", mt: "2px", maxWidth: 640 }}>
            Choose where the Shopify widget appears inside Zoho CRM records. One
            entry per Zoho module — needed for the widget to find the linked
            Shopify record.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          onClick={() => openModal(null)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            px: 1.75,
            py: 0.75,
            bgcolor: "#0f172a",
            boxShadow: "none",
            "&:hover": { bgcolor: "#000", boxShadow: "none" },
          }}
        >
          + Add Record View
        </Button>
      </Box>

      {/* Filters */}
      {!loading && configs.length > 0 && (
        <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Search module or field…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17, color: "#a1a1aa" }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 260, flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel sx={{ fontSize: 13 }}>Module</InputLabel>
            <Select
              label="Module"
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
              sx={{ fontSize: 13 }}
            >
              <MenuItem value="all" sx={{ fontSize: 13 }}>
                All modules
              </MenuItem>
              {moduleOptions.map((m) => (
                <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Table / empty states */}
      {loading ? (
        <Box sx={{ textAlign: "center", py: 8 }}>
          <CircularProgress size={28} sx={{ color: "#71717a" }} />
          <Typography sx={{ fontSize: 14, color: "#71717a", mt: 1.5 }}>
            Loading record views…
          </Typography>
        </Box>
      ) : configs.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            px: 4,
            bgcolor: "#fff",
            border: "1px dashed #e4e4e7",
            borderRadius: "12px",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: "#f0f9ff",
              color: "#0369a1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 1.5,
            }}
          >
            <VisibilityOutlinedIcon sx={{ fontSize: 26 }} />
          </Box>
          <Typography
            sx={{
              fontFamily: FONT_HEADING,
              fontSize: 17,
              fontWeight: 600,
              color: "#09090b",
              mb: "6px",
            }}
          >
            No record views yet
          </Typography>
          <Typography
            sx={{ fontSize: 14, color: "#71717a", maxWidth: 420, mx: "auto", mb: 2 }}
          >
            Add a record view to make the Shopify widget appear on a Zoho module
            (e.g. Contacts, Sales Orders, Products).
          </Typography>
          <Button
            variant="contained"
            onClick={() => openModal(null)}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              bgcolor: "#0f172a",
              boxShadow: "none",
              "&:hover": { bgcolor: "#000", boxShadow: "none" },
            }}
          >
            + Add Record View
          </Button>
        </Box>
      ) : displayedConfigs.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8, color: "#71717a" }}>
          <Typography sx={{ fontSize: 15, fontWeight: 500, color: "#09090b" }}>
            No record views match the current filters
          </Typography>
        </Box>
      ) : (
        <TableContainer
          sx={{
            border: "1px solid #e4e4e7",
            borderRadius: "12px",
            overflow: "hidden",
            bgcolor: "#fff",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCellSx}>Zoho Module</TableCell>
                <TableCell sx={headCellSx}>Shopify ID Field</TableCell>
                <TableCell sx={headCellSx}>Shop URL Field</TableCell>
                <TableCell sx={{ ...headCellSx, width: 110 }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedConfigs.map((c) => (
                <TableRow
                  key={c.id}
                  sx={{ "&:last-child td": { borderBottom: 0 } }}
                >
                  <TableCell sx={cellSx}>
                    <Chip
                      label={c.module || "—"}
                      size="small"
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        height: 24,
                        bgcolor: "#eff6ff",
                        color: "#1d4ed8",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Tooltip title={c.IdField || ""} placement="top-start" arrow>
                      <Typography sx={{ fontSize: 14, color: "#09090b" }}>
                        {prettifyFieldName(c.IdField) || "—"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Tooltip title={c.shopUrlField || ""} placement="top-start" arrow>
                      <Typography sx={{ fontSize: 14, color: "#09090b" }}>
                        {prettifyFieldName(c.shopUrlField) || "—"}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => openModal(c.id)}
                        sx={{ color: "#3f3f46" }}
                      >
                        <EditIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => setDeleteTarget(c)}
                        sx={{ color: "#ef4444" }}
                      >
                        <DeleteIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && configs.length > 0 && (
        <Typography
          sx={{ fontSize: 13, color: "#a1a1aa", mt: 1.5, textAlign: "right" }}
        >
          {displayedConfigs.length} of {configs.length} record view
          {configs.length !== 1 ? "s" : ""}
        </Typography>
      )}

      {/* ── Create / edit modal ── */}
      <Dialog
        open={modalOpen}
        onClose={closeModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "14px", overflow: "hidden" } }}
      >
        {/* Modal header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            py: 2,
            borderBottom: "1px solid #e4e4e7",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: FONT_HEADING,
                fontSize: 17,
                fontWeight: 700,
                color: "#09090b",
                letterSpacing: "-0.02em",
              }}
            >
              {editingId ? "Edit Record View" : "Add Record View"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#71717a", mt: "2px" }}>
              {editingId
                ? "Update which Zoho fields link this module to a Shopify record."
                : "Tell us which Zoho module to add the Shopify widget to."}
            </Typography>
          </Box>
          <IconButton onClick={closeModal} size="small" sx={{ color: "#71717a" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Modal body */}
        <Box sx={{ px: 3, py: 2.5, display: "flex", flexDirection: "column", gap: 2.25 }}>
          {/* Info card */}
          <Box
            sx={{
              display: "flex",
              gap: 1.25,
              p: "10px 12px",
              bgcolor: "#f0f9ff",
              border: "1px solid #bae6fd",
              borderRadius: "8px",
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18, color: "#0369a1", flexShrink: 0, mt: "1px" }} />
            <Typography sx={{ fontSize: 13, color: "#075985", lineHeight: 1.55 }}>
              A record view tells the Shopify widget which Zoho fields to read to
              find the matching Shopify record. The defaults work for most setups
              — only change them if you renamed the fields.
            </Typography>
          </Box>

          {/* Zoho module */}
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#09090b", mb: "4px" }}>
              Zoho module
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#71717a", mb: 1 }}>
              The Zoho CRM module where the Shopify widget will appear (e.g.
              Contacts, Sales Orders, Products).
            </Typography>
            <Autocomplete
              size="small"
              options={editingId ? zohoModules : availableModules}
              getOptionLabel={(opt) =>
                opt?.plural_label || opt?.api_name || opt || ""
              }
              value={
                zohoModules.find((m) => (m.api_name || m) === moduleName) ||
                (moduleName ? { api_name: moduleName } : null)
              }
              onChange={(_, newVal) =>
                setModuleName(newVal ? newVal.api_name || newVal : "")
              }
              disabled={!!editingId}
              renderInput={(params) => (
                <TextField {...params} placeholder="— Select a module —" />
              )}
              isOptionEqualToValue={(opt, val) =>
                (opt.api_name || opt) === (val.api_name || val)
              }
            />
          </Box>

          {/* Shopify resource — custom modules only. Standard modules resolve
              their renderer from the entity registry; a custom module must
              declare which Shopify data it mirrors. */}
          {isCustomModule && (
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#09090b", mb: "4px" }}>
                Shopify data to display
              </Typography>
              <Typography sx={{ fontSize: 13, color: "#71717a", mb: 1 }}>
                "{moduleName}" is a custom module, so choose which Shopify
                record type the widget should show on it.
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={resource}
                  onChange={(e) => setResource(e.target.value)}
                  displayEmpty
                  sx={{ fontSize: 14 }}
                >
                  <MenuItem value="" disabled sx={{ fontSize: 14 }}>
                    — Select Shopify data —
                  </MenuItem>
                  {RESOURCE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 14 }}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* ID field */}
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#09090b", mb: "4px" }}>
              Field that stores the Shopify record ID
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#71717a", mb: 1 }}>
              We'll use this Zoho field to look up the linked Shopify record. By
              default this is{" "}
              <Box component="code" sx={{ fontSize: 12, bgcolor: "#f4f4f5", px: "4px", py: "1px", borderRadius: "3px" }}>
                {prettifyFieldName(DEFAULT_ID_FIELD)}
              </Box>
              .
            </Typography>
            <Autocomplete
              size="small"
              options={fieldOptionsForIdField}
              value={idField || null}
              inputValue={idFieldDisplay}
              onChange={(_, newVal) => {
                setIdField(newVal || "");
                setIdFieldDisplay(prettifyFieldName(newVal || ""));
              }}
              onInputChange={(_, newInputVal, reason) => {
                if (reason === "input") {
                  setIdFieldDisplay(newInputVal);
                  setIdField(newInputVal);
                }
              }}
              loading={fieldsLoading}
              freeSolo
              getOptionLabel={(opt) => prettifyFieldName(opt) || ""}
              renderOption={(props, opt) => (
                <li {...props}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: 14, color: "#09090b" }}>
                      {prettifyFieldName(opt)}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#a1a1aa" }}>
                      {opt}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} placeholder="Pick a Zoho field" />
              )}
            />
          </Box>

          {/* Shop URL field */}
          <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#09090b", mb: "4px" }}>
              Field that stores the Shopify store URL
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#71717a", mb: 1 }}>
              Used when you have multiple Shopify stores connected — tells the
              widget which store this record belongs to.
            </Typography>
            <Autocomplete
              size="small"
              options={fieldOptionsForShopUrl}
              value={shopUrlField || null}
              inputValue={shopUrlFieldDisplay}
              onChange={(_, newVal) => {
                setShopUrlField(newVal || "");
                setShopUrlFieldDisplay(prettifyFieldName(newVal || ""));
              }}
              onInputChange={(_, newInputVal, reason) => {
                if (reason === "input") {
                  setShopUrlFieldDisplay(newInputVal);
                  setShopUrlField(newInputVal);
                }
              }}
              loading={fieldsLoading}
              freeSolo
              getOptionLabel={(opt) => prettifyFieldName(opt) || ""}
              renderOption={(props, opt) => (
                <li {...props}>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography sx={{ fontSize: 14, color: "#09090b" }}>
                      {prettifyFieldName(opt)}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: "#a1a1aa" }}>
                      {opt}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} placeholder="Pick a Zoho field" />
              )}
            />
          </Box>
        </Box>

        {/* Modal footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 3,
            py: 1.75,
            borderTop: "1px solid #e4e4e7",
            bgcolor: "#fafafa",
          }}
        >
          <Button
            onClick={closeModal}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              color: "#3f3f46",
              borderColor: "#e4e4e7",
              "&:hover": { borderColor: "#a1a1aa", bgcolor: "#fafafa" },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saving || !moduleName}
            onClick={handleSave}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: 14,
              bgcolor: "#0f172a",
              boxShadow: "none",
              "&:hover": { bgcolor: "#000", boxShadow: "none" },
            }}
          >
            {saving && <CircularProgress size={14} sx={{ mr: 1, color: "#fff" }} />}
            {editingId ? "Save Changes" : "Create Record View"}
          </Button>
        </Box>
      </Dialog>

      {/* ── Delete confirm dialog (redesigned to match the rest) ── */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        PaperProps={{
          sx: { borderRadius: "14px", maxWidth: 460, width: "100%", overflow: "hidden" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              bgcolor: "#fee2e2",
              color: "#dc2626",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <WarningAmberOutlinedIcon sx={{ fontSize: 26 }} />
          </Box>

          <Typography
            sx={{
              fontFamily: FONT_HEADING,
              fontSize: 17,
              fontWeight: 700,
              color: "#09090b",
              letterSpacing: "-0.02em",
              mb: 0.5,
            }}
          >
            Delete the{" "}
            <Box component="span" sx={{ color: "#0f172a" }}>
              {deleteTarget?.module || "this module"}
            </Box>{" "}
            record view?
          </Typography>

          <Typography sx={{ fontSize: 14, color: "#52525b", lineHeight: 1.55, mb: 2 }}>
            The Shopify widget will stop appearing on{" "}
            <strong>{deleteTarget?.module || "this module"}</strong> records
            until a new record view is created.
          </Typography>

          <Typography sx={{ fontSize: 13, color: "#71717a", mb: 2.5 }}>
            Existing Zoho CRM records won't be affected. You can re-create this
            record view any time.
          </Typography>

          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              onClick={() => setDeleteTarget(null)}
              variant="outlined"
              disabled={deleting}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                color: "#3f3f46",
                borderColor: "#e4e4e7",
                "&:hover": { borderColor: "#a1a1aa", bgcolor: "#fafafa" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              variant="contained"
              disabled={deleting}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 14,
                bgcolor: "#dc2626",
                boxShadow: "none",
                "&:hover": { bgcolor: "#b91c1c", boxShadow: "none" },
              }}
            >
              {deleting && <CircularProgress size={14} sx={{ mr: 1, color: "#fff" }} />}
              Delete Record View
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}
